import React, { useEffect, useState } from 'react';
import {
  Lock, LogOut, Upload, Trash2, Save, RefreshCw, Mail, Calendar, QrCode, ImagePlus,
  Star, Check, X, MessageCircle, FileDown, Bell,
} from 'lucide-react';
import { adminApi, publicApi, getToken, setToken, clearToken, API } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import LotusLogo from '../components/LotusLogo';
import { contactInfo } from '../data/mock';

const Login = ({ onOk }) => {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await publicApi.post('/admin/login', { password: pw });
      setToken(r.data.token);
      onOk();
    } catch { setErr('Incorrect password'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg,#f4a48c,#e9a0aa 30%,#d29ac1 60%,#b8a4d8 85%,#a5b0e6)' }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white/20 backdrop-blur-md border border-white/30 rounded-3xl p-8 shadow-2xl">
        <div className="flex justify-center mb-4"><LotusLogo className="w-20 h-20" /></div>
        <h1 className="text-white text-center font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.9rem' }}>Admin</h1>
        <p className="text-center text-white/80 mt-1 tracking-[0.3em] text-xs" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>BLISSFULL MOBILE SPA</p>
        <label className="block text-white/80 text-xs tracking-[0.25em] mb-2 mt-8" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>PASSWORD</label>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
          className="w-full bg-transparent border-b border-white/40 focus:border-white text-white py-2 outline-none"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
        {err && <p className="text-rose-100 mt-3 text-sm">{err}</p>}
        <button disabled={busy} type="submit"
          className="mt-8 w-full inline-flex justify-center items-center gap-2 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all disabled:opacity-60"
          style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
          <Lock size={14} /> {busy ? 'SIGNING IN...' : 'SIGN IN'}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [settings, setSettings] = useState({ calendar_embed_url: '', calendar_book_url: '', calendly_45: '', calendly_60: '', calendly_90: '', snapscan_link: '', snapscan_qr_base64: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  const [tab, setTab] = useState('reminders');

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    adminApi.get('/admin/verify').then(() => setAuthed(true)).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (authed) loadAll(); /* eslint-disable-next-line */ }, [authed]);

  const loadAll = async () => {
    try {
      const [p, b, u, v, r, s] = await Promise.all([
        publicApi.get('/photos'),
        adminApi.get('/admin/bookings'),
        adminApi.get('/admin/upcoming'),
        adminApi.get('/admin/vouchers'),
        adminApi.get('/admin/reviews'),
        publicApi.get('/settings'),
      ]);
      setPhotos(p.data); setBookings(b.data); setUpcoming(u.data);
      setVouchers(v.data); setReviews(r.data); setSettings(s.data);
    } catch { toast({ title: 'Failed to load', description: 'Please refresh.' }); }
  };

  const logout = async () => { try { await adminApi.post('/admin/logout'); } catch {} clearToken(); setAuthed(false); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('caption', caption);
      await adminApi.post('/admin/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCaption('');
      toast({ title: 'Photo uploaded', description: 'It is now live on the gallery.' });
      const r = await publicApi.get('/photos'); setPhotos(r.data);
    } catch (err) { toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Please try a smaller image.' }); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removePhoto = async (id) => {
    try { await adminApi.delete(`/admin/photos/${id}`); setPhotos(photos.filter((p) => p.id !== id)); toast({ title: 'Photo removed' }); }
    catch { toast({ title: 'Could not delete' }); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try { const r = await adminApi.post('/admin/settings', settings); setSettings(r.data); toast({ title: 'Saved \u2728' }); }
    catch { toast({ title: 'Save failed' }); }
    finally { setSavingSettings(false); }
  };

  const testEmail = async () => {
    try { const r = await adminApi.post('/admin/test-email'); toast({ title: r.data.sent ? 'Test email sent \u2728' : 'Not sent', description: r.data.reason || 'Check your inbox.' }); }
    catch { toast({ title: 'Failed to send' }); }
  };

  const sendDigest = async () => {
    try { const r = await adminApi.post('/admin/send-daily-digest'); toast({ title: r.data.sent ? 'Digest sent to Dawn \u2728' : (r.data.reason || 'Nothing to send') }); }
    catch { toast({ title: 'Failed to send digest' }); }
  };

  const markPaid = async (vid) => {
    try {
      const r = await adminApi.post(`/admin/vouchers/${vid}/mark-paid`);
      setVouchers(vouchers.map(v => v.id === vid ? { ...v, paid: true, pdf_sent: r.data.pdf_sent } : v));
      toast({ title: 'Marked as paid', description: r.data.pdf_sent ? 'PDF voucher emailed to recipient.' : (r.data.reason || 'PDF not emailed (check GMAIL_APP_PASSWORD).') });
    } catch { toast({ title: 'Could not update voucher' }); }
  };

  const downloadPdf = async (vid, code) => {
    try {
      const r = await adminApi.get(`/admin/vouchers/${vid}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `voucher-${code}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast({ title: 'Failed to download PDF' }); }
  };

  const approveReview = async (id) => {
    try { await adminApi.post(`/admin/reviews/${id}/approve`); setReviews(reviews.map(r => r.id === id ? { ...r, approved: true } : r)); toast({ title: 'Review approved & published \u2728' }); }
    catch { toast({ title: 'Could not approve' }); }
  };
  const deleteReview = async (id) => {
    try { await adminApi.delete(`/admin/reviews/${id}`); setReviews(reviews.filter(r => r.id !== id)); toast({ title: 'Review deleted' }); }
    catch { toast({ title: 'Could not delete' }); }
  };

  const readSnapscanQr = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSettings({ ...settings, snapscan_qr_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const remindClient = (b) => {
    const msg = encodeURIComponent(
      `Hi ${b.name}! Friendly reminder from Blissfull Mobile Spa: your ${b.treatment || 'treatment'} is booked for ${b.date}${b.time ? ` at ${b.time}` : ''}. See you soon! \u2014 Dawn`
    );
    // Prefer WhatsApp deep link (phone) if provided
    if (b.phone) {
      const cleaned = b.phone.replace(/[^0-9+]/g, '').replace(/^0/, '27');
      window.open(`https://wa.me/${cleaned}?text=${msg}`, '_blank');
    } else {
      window.location.href = `mailto:${b.email}?subject=${encodeURIComponent('Your treatment tomorrow')}&body=${msg}`;
    }
  };

  if (checking) return <div className="min-h-screen bg-neutral-900 flex items-center justify-center text-white">Loading...</div>;
  if (!authed) return (<><Login onOk={() => setAuthed(true)} /><Toaster /></>);

  const pendingReviews = reviews.filter(r => !r.approved).length;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-800" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex-shrink-0"><LotusLogo className="w-10 h-10" /></div>
            <div>
              <p className="tracking-[0.25em] text-[10px] text-neutral-500">BLISSFULL MOBILE SPA</p>
              <h1 className="font-light text-lg leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="p-2 rounded-full hover:bg-neutral-100" aria-label="Refresh"><RefreshCw size={16} /></button>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-300 text-sm hover:bg-neutral-100">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-2 border-t border-neutral-100 overflow-x-auto">
          {[
            { id: 'reminders', label: `Reminders (${upcoming.length})` },
            { id: 'bookings', label: `Bookings (${bookings.length})` },
            { id: 'vouchers', label: `Vouchers (${vouchers.length})` },
            { id: 'reviews', label: `Reviews${pendingReviews ? ` \u2022 ${pendingReviews} new` : ''}` },
            { id: 'photos', label: 'Photos' },
            { id: 'settings', label: 'Settings' },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap px-4 py-3 text-sm border-b-2 transition-colors ${
                tab === t.id ? 'border-rose-500 text-rose-600' : 'border-transparent text-neutral-500 hover:text-neutral-800'
              }`}>{t.label}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === 'reminders' && (
          <section>
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 mb-6">
              <h2 className="text-lg font-medium mb-1 flex items-center gap-2"><Bell size={18} /> Today &amp; tomorrow</h2>
              <p className="text-sm text-neutral-500 mb-4">One-tap WhatsApp reminder to each client. Dawn also gets an emailed digest daily at 08:00.</p>
              <button onClick={sendDigest} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm">
                <Mail size={14} /> Email me today&apos;s digest now
              </button>
            </div>

            {upcoming.length === 0 && <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center text-neutral-500">No bookings scheduled for today or tomorrow.</div>}
            <div className="space-y-3">
              {upcoming.map((b) => (
                <div key={b.id} className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-rose-500">{b.date} {b.time && `@ ${b.time}`}</p>
                    <p className="font-medium mt-1">{b.name} <span className="text-neutral-400 font-normal">\u2014 {b.treatment || 'Treatment TBC'}</span></p>
                    <p className="text-sm text-neutral-500">{b.phone || b.email}</p>
                  </div>
                  <button onClick={() => remindClient(b)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100 text-sm">
                    <MessageCircle size={14} /> Send reminder
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === 'photos' && (
          <section>
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 mb-8">
              <h2 className="text-lg font-medium mb-1 flex items-center gap-2"><ImagePlus size={18} /> Upload treatment photo</h2>
              <p className="text-sm text-neutral-500 mb-4">Photos appear instantly at the top of the public gallery. Max 8 MB, JPEG/PNG.</p>
              <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-3" />
              <label className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm cursor-pointer ${uploading ? 'bg-neutral-400' : 'bg-rose-500 hover:bg-rose-600'}`}>
                <Upload size={14} /> {uploading ? 'Uploading...' : 'Choose photo'}
                <input type="file" accept="image/*" onChange={upload} className="hidden" disabled={uploading} />
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.length === 0 && (<p className="text-neutral-500 col-span-full">No admin photos yet.</p>)}
              {photos.map((p) => (
                <div key={p.id} className="relative rounded-xl overflow-hidden border border-neutral-200 group">
                  <img src={p.data_url} alt={p.caption} className="w-full h-40 object-cover" />
                  <button onClick={() => removePhoto(p.id)} className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete photo">
                    <Trash2 size={14} />
                  </button>
                  {p.caption && <p className="text-xs px-2 py-1 truncate bg-white">{p.caption}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === 'bookings' && (
          <section className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 text-left">
                <tr><th className="p-3">When</th><th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">Treatment</th><th className="p-3">Preferred</th><th className="p-3">Message</th><th className="p-3">Email</th></tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (<tr><td colSpan="7" className="p-6 text-center text-neutral-500">No bookings yet.</td></tr>)}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-neutral-100 align-top">
                    <td className="p-3 text-neutral-500 whitespace-nowrap">{new Date(b.created_at).toLocaleString('en-ZA')}</td>
                    <td className="p-3 font-medium">{b.name}</td>
                    <td className="p-3">{b.email}<br/><span className="text-neutral-500">{b.phone}</span></td>
                    <td className="p-3">{b.treatment || '-'}</td>
                    <td className="p-3">{b.date || '-'} {b.time && `@ ${b.time}`}</td>
                    <td className="p-3 max-w-xs">{b.message}</td>
                    <td className="p-3">{b.email_sent ? <span className="text-green-600">Sent</span> : <span className="text-amber-600">Not sent</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === 'vouchers' && (
          <section className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 text-left">
                <tr><th className="p-3">When</th><th className="p-3">Code</th><th className="p-3">Buyer</th><th className="p-3">Recipient</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
              </thead>
              <tbody>
                {vouchers.length === 0 && (<tr><td colSpan="7" className="p-6 text-center text-neutral-500">No vouchers yet.</td></tr>)}
                {vouchers.map((v) => (
                  <tr key={v.id} className="border-t border-neutral-100">
                    <td className="p-3 text-neutral-500 whitespace-nowrap">{new Date(v.created_at).toLocaleString('en-ZA')}</td>
                    <td className="p-3 tracking-widest">{v.code}</td>
                    <td className="p-3">{v.buyer_name}<br/><span className="text-neutral-500">{v.buyer_email}</span></td>
                    <td className="p-3">{v.recipient_name}<br/><span className="text-neutral-500 text-xs">{v.recipient_email || '-'}</span></td>
                    <td className="p-3 font-medium">R{Number(v.amount).toLocaleString()}</td>
                    <td className="p-3">
                      {v.paid ? <span className="text-green-600">Paid</span> : <span className="text-amber-600">Awaiting payment</span>}
                      {v.paid && v.pdf_sent && <div className="text-xs text-green-600 mt-1">PDF emailed</div>}
                    </td>
                    <td className="p-3 space-x-3">
                      <button onClick={() => downloadPdf(v.id, v.code)} className="text-neutral-700 hover:underline inline-flex items-center gap-1"><FileDown size={13} /> PDF</button>
                      {!v.paid && <button onClick={() => markPaid(v.id)} className="text-rose-600 hover:underline">Mark paid</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === 'reviews' && (
          <section>
            {reviews.length === 0 && <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center text-neutral-500">No reviews yet.</div>}
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-1 text-amber-500">
                        {Array.from({ length: r.stars }).map((_, i) => (<Star key={i} size={14} fill="currentColor" strokeWidth={0} />))}
                      </div>
                      <p className="font-medium mt-1">{r.name} <span className="text-neutral-400 font-normal">\u2014 {r.treatment || 'Treatment'}</span></p>
                    </div>
                    <div className="flex gap-2">
                      {!r.approved && (
                        <button onClick={() => approveReview(r.id)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs">
                          <Check size={13} /> Approve
                        </button>
                      )}
                      <button onClick={() => deleteReview(r.id)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-neutral-300 hover:bg-neutral-100 text-xs">
                        <X size={13} /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 italic text-neutral-700">&ldquo;{r.text}&rdquo;</p>
                  <p className="mt-2 text-xs text-neutral-400">{new Date(r.created_at).toLocaleString('en-ZA')} {r.approved ? '\u2022 Published' : '\u2022 Pending'}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === 'settings' && (
          <section className="space-y-6">
            <div className="bg-white border border-neutral-200 rounded-2xl p-6">
              <h2 className="text-lg font-medium mb-1 flex items-center gap-2"><Calendar size={18} /> Booking calendar (Calendly)</h2>
              <p className="text-sm text-neutral-500 mb-3">
                Paste a separate Calendly URL for each treatment length so guests can pick 45/60/90 minutes right on the site.
                In Calendly, create 3 event types (e.g. "45-min Bliss Massage", "60-min Total Time Out", "90-min Full Escape"), then paste the share link for each below.
                Add <code>?primary_color=ff00eb</code> at the end to keep the pink accent.
              </p>
              <label className="block text-xs text-neutral-500 mt-3">45-minute Bliss Massage</label>
              <input value={settings.calendly_45} onChange={(e) => setSettings({ ...settings, calendly_45: e.target.value })}
                placeholder="https://calendly.com/dawnfelicityatkhumalo/45-min-bliss?primary_color=ff00eb"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-3" />
              <label className="block text-xs text-neutral-500">60-minute Total Time Out</label>
              <input value={settings.calendly_60} onChange={(e) => setSettings({ ...settings, calendly_60: e.target.value })}
                placeholder="https://calendly.com/dawnfelicityatkhumalo/60-min-timeout?primary_color=ff00eb"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-3" />
              <label className="block text-xs text-neutral-500">90-minute Full Escapes</label>
              <input value={settings.calendly_90} onChange={(e) => setSettings({ ...settings, calendly_90: e.target.value })}
                placeholder="https://calendly.com/dawnfelicityatkhumalo/90-min-full-escape?primary_color=ff00eb"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg" />
              <p className="text-xs text-neutral-500 mt-4">
                <b>Branding tip:</b> Confirmation emails, event descriptions and the logo shown at the top of the booking page are all controlled inside your Calendly account
                (<i>Event type &rarr; Confirmation page &amp; Notifications</i>). Upload your Blissfull Mobile Spa logo there and add a warm welcome message so guest emails feel on-brand.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl p-6">
              <h2 className="text-lg font-medium mb-1 flex items-center gap-2"><QrCode size={18} /> SnapScan payment</h2>
              <label className="block text-xs text-neutral-500 mt-2">SnapScan link</label>
              <input value={settings.snapscan_link} onChange={(e) => setSettings({ ...settings, snapscan_link: e.target.value })}
                placeholder="https://pos.snapscan.io/qr/XXXX"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-4" />
              <label className="block text-xs text-neutral-500">SnapScan QR image (PNG/JPEG)</label>
              <input type="file" accept="image/*" onChange={readSnapscanQr} className="mt-1 text-sm" />
              {settings.snapscan_qr_base64 && (
                <div className="mt-3 flex items-center gap-3">
                  <img src={settings.snapscan_qr_base64} alt="QR" className="w-24 h-24 border rounded-lg" />
                  <button onClick={() => setSettings({ ...settings, snapscan_qr_base64: '' })} className="text-sm text-rose-600 hover:underline">Remove</button>
                </div>
              )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl p-6">
              <h2 className="text-lg font-medium mb-2 flex items-center gap-2"><Mail size={18} /> Email delivery</h2>
              <p className="text-sm text-neutral-500 mb-4">Emails go to <b>{contactInfo.email}</b> via Gmail SMTP. Set <code>GMAIL_APP_PASSWORD</code> in the backend .env once you have an App Password from your Google account.</p>
              <button onClick={testEmail} className="px-5 py-2 rounded-full bg-neutral-900 text-white text-sm hover:bg-neutral-800">Send test email</button>
            </div>

            <div className="flex justify-end">
              <button onClick={saveSettings} disabled={savingSettings}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm disabled:opacity-60">
                <Save size={14} /> {savingSettings ? 'Saving...' : 'Save settings'}
              </button>
            </div>
          </section>
        )}
      </main>
      <Toaster />
    </div>
  );
};

export default Admin;
